import React, { useId } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import i18n from '../../lib/i18n';

/**
 * Lightweight toolbar: search, optional column filters, counts, compact, export (client-side handlers).
 * @param {{
 *   search?: string,
 *   onSearchChange?: (v: string) => void,
 *   searchPlaceholderKey?: string,
 *   filters?: Array<{ id: string, label: string, value: string, onChange: (v: string) => void, options?: Array<{ value: string, label: string }> }>,
 *   filteredCount?: number,
 *   totalCount?: number,
 *   compact?: boolean,
 *   onCompactChange?: (v: boolean) => void,
 *   onExportCsv?: () => void,
 *   onExportJson?: () => void,
 *   onCopyFilteredJson?: () => void,
 * }} props
 */
export default function AdminTableToolbar({
  search,
  onSearchChange,
  searchPlaceholderKey = 'adminExport.searchPlaceholder',
  filters = [],
  filteredCount,
  totalCount,
  compact,
  onCompactChange,
  onExportCsv,
  onExportJson,
  onCopyFilteredJson,
}) {
  const { t } = useLanguage();
  const uid = useId();
  const searchId = `${uid}-search`;

  const showCounts = filteredCount != null && totalCount != null;
  const countLabel = showCounts
    ? i18n.t('adminExport.resultCount', { filtered: filteredCount, total: totalCount })
    : filteredCount != null
      ? i18n.t('adminExport.resultCountSimple', { n: filteredCount })
      : null;

  return (
    <div className="admin-hub-kit-toolbar" role="region" aria-label={t('adminExport.toolbarAria')}>
      <div className="admin-hub-kit-toolbar__row">
        {onSearchChange ? (
          <div className="admin-hub-kit-toolbar__search">
            <label htmlFor={searchId} className="admin-hub-kit-toolbar__label">
              {t('adminExport.search')}
            </label>
            <input
              id={searchId}
              type="search"
              className="admin-hub-kit-toolbar__input"
              value={search ?? ''}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder={t(searchPlaceholderKey)}
              autoComplete="off"
            />
          </div>
        ) : null}

        {countLabel ? (
          <div className="admin-hub-kit-toolbar__counts" role="status">
            {countLabel}
          </div>
        ) : null}

        <div className="admin-hub-kit-toolbar__actions">
          {onCompactChange ? (
            <label className="admin-hub-kit-toolbar__compact">
              <input type="checkbox" checked={!!compact} onChange={(e) => onCompactChange(e.target.checked)} />
              <span>{t('adminExport.compactMode')}</span>
            </label>
          ) : null}
          {onExportCsv ? (
            <button type="button" className="admin-hub-btn admin-hub-btn--secondary" onClick={onExportCsv}>
              {t('adminExport.exportCsv')}
            </button>
          ) : null}
          {onExportJson ? (
            <button type="button" className="admin-hub-btn admin-hub-btn--secondary" onClick={onExportJson}>
              {t('adminExport.exportJson')}
            </button>
          ) : null}
          {onCopyFilteredJson ? (
            <button type="button" className="admin-hub-btn admin-hub-btn--secondary" onClick={onCopyFilteredJson}>
              {t('adminExport.copyJson')}
            </button>
          ) : null}
        </div>
      </div>

      {filters.length ? (
        <div className="admin-hub-kit-toolbar__filters">
          <span className="admin-hub-kit-toolbar__filters-title">{t('adminExport.filter')}</span>
          {filters.map((f) => {
            const fid = `${uid}-f-${f.id}`;
            if (f.options?.length) {
              return (
                <div key={f.id} className="admin-hub-kit-toolbar__filter-field">
                  <label htmlFor={fid} className="admin-hub-kit-toolbar__label">
                    {f.label}
                  </label>
                  <select id={fid} className="admin-hub-kit-toolbar__select" value={f.value} onChange={(e) => f.onChange(e.target.value)}>
                    {f.options.map((o) => (
                      <option key={o.value || '__all'} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
              );
            }
            return (
              <div key={f.id} className="admin-hub-kit-toolbar__filter-field">
                <label htmlFor={fid} className="admin-hub-kit-toolbar__label">
                  {f.label}
                </label>
                <input
                  id={fid}
                  type="text"
                  className="admin-hub-kit-toolbar__input admin-hub-kit-toolbar__input--narrow"
                  value={f.value}
                  onChange={(e) => f.onChange(e.target.value)}
                  autoComplete="off"
                />
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
